import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface EtsyConnection {
  id: string;
  user_id: string;
  shop_id: string | null;
  shop_name: string | null;
  token_expires_at: string;
  connected_at: string;
  is_active: boolean;
}

interface EtsyListing {
  listing_id: number;
  title: string;
  description: string;
  price: { amount: number; divisor: number; currency_code: string };
  quantity: number;
  state: string;
  url: string;
}

export const useEtsyIntegration = () => {
  const [connection, setConnection] = useState<EtsyConnection | null>(null);
  const [listings, setListings] = useState<EtsyListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const { toast } = useToast();

  // Store PKCE codes in sessionStorage during OAuth flow
  const storePKCECodes = (state: string, codeVerifier: string) => {
    sessionStorage.setItem(`etsy_state_${state}`, codeVerifier);
  };

  const getPKCECode = (state: string): string | null => {
    return sessionStorage.getItem(`etsy_state_${state}`);
  };

  const clearPKCECode = (state: string) => {
    sessionStorage.removeItem(`etsy_state_${state}`);
  };

  const fetchConnection = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setConnection(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('etsy_connections')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      setConnection(data);
    } catch (error: any) {
      console.error('Error fetching Etsy connection:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  const initiateConnect = async () => {
    try {
      setConnecting(true);
      
      const redirectUri = `${window.location.origin}/settings?etsy_callback=true`;
      
      const { data, error } = await supabase.functions.invoke('etsy-oauth', {
        body: {
          action: 'get_auth_url',
          redirect_uri: redirectUri,
        },
      });

      if (error || !data.success) {
        throw new Error(data?.error || 'Failed to get auth URL');
      }

      // Store PKCE verifier
      storePKCECodes(data.state, data.code_verifier);

      // Redirect to Etsy OAuth
      window.location.href = data.auth_url;
    } catch (error: any) {
      console.error('Error initiating Etsy connection:', error);
      toast({
        title: 'Connection Failed',
        description: error.message,
        variant: 'destructive',
      });
      setConnecting(false);
    }
  };

  const completeConnect = async (code: string, state: string) => {
    try {
      setConnecting(true);
      
      const codeVerifier = getPKCECode(state);
      if (!codeVerifier) {
        throw new Error('OAuth state mismatch. Please try again.');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const redirectUri = `${window.location.origin}/settings?etsy_callback=true`;

      const { data, error } = await supabase.functions.invoke('etsy-oauth', {
        body: {
          action: 'exchange_code',
          code,
          code_verifier: codeVerifier,
          redirect_uri: redirectUri,
          user_id: user.id,
        },
      });

      if (error || !data.success) {
        throw new Error(data?.error || 'Failed to connect Etsy account');
      }

      clearPKCECode(state);
      await fetchConnection();

      toast({
        title: 'Connected Successfully',
        description: `Connected to Etsy shop: ${data.shop_name || 'Your shop'}`,
      });
    } catch (error: any) {
      console.error('Error completing Etsy connection:', error);
      toast({
        title: 'Connection Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.functions.invoke('etsy-oauth', {
        body: {
          action: 'disconnect',
          user_id: user.id,
        },
      });

      if (error) throw error;

      setConnection(null);
      setListings([]);

      toast({
        title: 'Disconnected',
        description: 'Your Etsy account has been disconnected.',
      });
    } catch (error: any) {
      console.error('Error disconnecting Etsy:', error);
      toast({
        title: 'Disconnect Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const fetchListings = async (limit = 25, offset = 0) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('etsy-api', {
        body: {
          action: 'get_listings',
          user_id: user.id,
          limit,
          offset,
        },
      });

      if (error || !data.success) {
        throw new Error(data?.error || 'Failed to fetch listings');
      }

      setListings(data.data.results || []);
      return data.data;
    } catch (error: any) {
      console.error('Error fetching Etsy listings:', error);
      toast({
        title: 'Failed to Fetch Listings',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const uploadImageToListing = async (
    listingId: number,
    imageBase64: string,
    rank = 1,
    altText?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Remove data URL prefix if present
      const base64Data = imageBase64.replace(/^data:image\/\w+;base64,/, '');

      const { data, error } = await supabase.functions.invoke('etsy-api', {
        body: {
          action: 'upload_listing_image',
          user_id: user.id,
          listing_id: listingId,
          image_base64: base64Data,
          rank,
          alt_text: altText,
        },
      });

      if (error || !data.success) {
        throw new Error(data?.error || 'Failed to upload image');
      }

      toast({
        title: 'Image Uploaded',
        description: 'Your image has been uploaded to Etsy successfully.',
      });

      return data.data;
    } catch (error: any) {
      console.error('Error uploading to Etsy:', error);
      toast({
        title: 'Upload Failed',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  return {
    connection,
    listings,
    loading,
    connecting,
    initiateConnect,
    completeConnect,
    disconnect,
    fetchListings,
    uploadImageToListing,
    refetch: fetchConnection,
  };
};
